import api from "../../config/api";
import { setLocalStorage } from "../../utils/LocalStorageHandler";
import LoggerService from "../../services/LoggerService";

const state = {
    user: null,
    token: null,
    sessionData: null,
    authError: null,
    loading: false,
};

const mutations = {
    SET_USER(state, payload) {
        setLocalStorage("userDetails", payload)
        state.user = payload;
    },
    SET_TOKEN(state, payload) {
        setLocalStorage("token", payload)
        state.token = payload;
    },
    SET_SESSION_DATA(state, payload) {
        setLocalStorage("sessionData", payload)
        state.sessionData = payload;
    },
    SET_ERROR(state, payload) {
        state.authError = payload;
    },
    SET_LOADING(state, payload) {
        state.loading = payload;
    },
    LOGOUT(state) {
        state.user = null;
        state.token = null;
        state.authError = null;
    },
};

const actions = {
    async login({ commit, dispatch }, { email, password }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post(`/auth/login`, {
                email,
                password,
            });
            commit("SET_USER", response.data.output.user || null);
            commit("SET_TOKEN", response.data.output.access_token || null);
            commit("SET_SESSION_DATA", response.data.output.session_details || null);
            commit("SET_LOADING", false);
            
            // Log successful login
            await LoggerService.logAuthAttempt(
                'LOGIN',
                email,
                true,
                null,
                {
                    userId: response.data.output.user?.personid,
                    locationId: response.data.output.session_details?.locationid
                }
            );
            
            // Fetch and cache help text after successful login
            try {
                await dispatch('fetchHelpText');
                console.log('Help text cached successfully after login');
            } catch (helpTextError) {
                console.error('Failed to cache help text after login:', helpTextError);
                // Don't throw error here - login was successful, help text is optional
            }
            
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Login failed";
            commit("SET_ERROR", errorMessage);
            commit("SET_LOADING", false);
            
            // Log failed login attempt
            await LoggerService.logAuthAttempt(
                'LOGIN',
                email,
                false,
                errorMessage
            );
            
            throw error;
        }
    },

    async register({ commit }, payload) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post(`/auth/register`, payload);
            // Do not set user or token during signup - only set during login
            commit("SET_LOADING", false);
            
            // Log successful registration
            await LoggerService.logAuthAttempt(
                'REGISTRATION',
                payload.email,
                true,
                null,
                {
                    userId: response.data.user?.personid,
                    firstname: payload.firstname,
                    lastname: payload.lastname,
                    country: payload.country,
                    registrationType: payload.registrationtype
                }
            );
            
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Registration failed";
            commit("SET_ERROR", errorMessage);
            commit("SET_LOADING", false);
            
            // Log failed registration attempt
            await LoggerService.logAuthAttempt(
                'REGISTRATION',
                payload.email,
                false,
                errorMessage,
                {
                    firstname: payload.firstname,
                    lastname: payload.lastname
                }
            );
            
            throw error;
        }
    },

    logout({ commit, dispatch }) {
        return (async () => {
            // Single backend API call: notify server and get logout message
            await dispatch('submitLogout');
            commit("LOGOUT");

            try {
                dispatch('clearHelpTextCache');
                console.log('Help text cache cleared on logout');
            } catch (error) {
                console.error('Failed to clear help text cache on logout:', error);
            }
        })();
    },

    async verifySignupOtp({ commit }, { email, otp }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post(`/auth/verify-otp`, {
                email: email,
                otp: otp,
                confirmation: true
            });
            commit("SET_LOADING", false);
            return response.data;
        } catch (error) {
            commit("SET_ERROR", error.response?.data?.message || "OTP verification failed");
            commit("SET_LOADING", false);
            throw error;
        }
    },
};

const getters = {
    isAuthenticated: state => !!state.token,
    currentUser: state => state.user,
    authToken: state => state.token,
    authError: state => state.authError,
    authLoading: state => state.loading,
};

export default {
    state,
    mutations,
    actions,
    getters,
};